<template>
  <div class="mt20 ml12" style="max-width: 760px">
    <h3>Index Gallery from Google Photos</h3>
    <p class="mt4" style="color: #666">
      Paste a Google Photos album link. We scrape it and add it to the live
      gallery. Re-indexing the same event/year replaces the old entry.
    </p>

    <div class="mt20">
      <label class="bold">Album link</label>
      <input
        v-model="url"
        type="text"
        class="gi_input"
        placeholder="https://photos.app.goo.gl/..."
      />
    </div>

    <div class="mt12 flex" style="gap: 16px">
      <div style="flex: 2">
        <label class="bold">Master event (optional)</label>
        <select v-model="masterEvent" class="gi_input">
          <option value="">None</option>
          <option v-for="me in masterEvents" :key="me.id" :value="me.id">
            {{ me.name }}
          </option>
        </select>
      </div>
      <div style="flex: 1">
        <label class="bold">Year (optional)</label>
        <input
          v-model="year"
          type="number"
          class="gi_input"
          placeholder="2026"
        />
      </div>
    </div>

    <button
      class="mt20 primary"
      :disabled="loading || !url"
      @click="submit"
    >
      {{ loading ? "Scraping (can take ~30-60s)..." : "Scrape & add to gallery" }}
    </button>

    <div v-if="result" class="mt20 gi_result">
      <h4>Added: {{ result.added.Title }}</h4>
      <p>{{ result.added.photos }} photos. {{ result.replaced ? "Replaced existing entry." : "New entry." }} Gallery now has {{ result.total }} albums.</p>
      <div
        v-if="result.added.cover"
        class="gi_cover"
        :style="{ background: 'url(' + result.added.cover + ') center / cover no-repeat' }"
      ></div>
    </div>

    <div class="mt40" v-if="rows.length">
      <h4>Currently indexed ({{ rows.length }})</h4>
      <p class="mt4" style="color: #666">
        Master event decides which pill an album shows under on the gallery
        page. Rows marked "inferred" are matched from the title at display
        time. Save them here to make the assignment explicit.
      </p>
      <table class="ui celled table mt8">
        <thead>
          <tr>
            <th>Title</th>
            <th>Photos</th>
            <th>Master event</th>
            <th>Year</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(g, i) in rows" :key="i">
            <td>
              {{ g.Title }}
              <span v-if="g.inferred" style="color: #999">(inferred)</span>
            </td>
            <td>{{ g.photoCount }}</td>
            <td>
              <select v-model="g.master_event" class="gi_input">
                <option value="">None</option>
                <option v-for="me in masterEvents" :key="me.id" :value="me.id">
                  {{ me.name }}
                </option>
              </select>
            </td>
            <td style="width: 90px">
              <input v-model="g.year" type="number" class="gi_input" />
            </td>
            <td style="width: 90px">
              <button :disabled="g.saving" @click="saveRow(g)">
                {{ g.saving ? "..." : "Save" }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
import {
  resolveGalleryMeta,
  OTHER_MASTER_EVENT_ID,
} from "../utils/galleryMeta";

export default {
  name: "gallery-index",
  data() {
    return {
      url: "",
      masterEvent: "",
      year: "",
      masterEvents: [],
      rows: [],
      loading: false,
      result: null,
    };
  },
  mounted() {
    this.$req
      .get("master-events?_limit=-1")
      .then((r) => {
        this.masterEvents = r.data;
        this.loadExisting();
      })
      .catch((e) => console.log(e));
  },
  methods: {
    loadExisting() {
      this.$req
        .get("gallery")
        .then((r) => {
          const list = (r.data && r.data.indexedPhotoUrls) || [];
          this.rows = list.map((g) => {
            const meta = resolveGalleryMeta(g, this.masterEvents);
            const resolved =
              meta.master_event === OTHER_MASTER_EVENT_ID ? "" : meta.master_event;
            return {
              Title: meta.Title,
              photoCount: (g.photos || []).length,
              master_event: resolved,
              year: meta.year || "",
              // Stored value does not already match what we display.
              inferred: g.master_event !== resolved,
              saving: false,
            };
          });
        })
        .catch((e) => console.log(e));
    },
    saveRow(row) {
      row.saving = true;
      this.$req
        .post(
          "gallery/reassign",
          {
            title: row.Title,
            master_event: row.master_event === "" ? "" : row.master_event,
            year: row.year === "" ? null : row.year,
          },
          this.$bus.headers
        )
        .then(() => {
          row.inferred = false;
          this.$bus.notify("Updated " + row.Title);
        })
        .catch((e) => {
          let msg =
            (e.response && e.response.data && e.response.data.message) ||
            e.message ||
            "Failed to update";
          this.$bus.notify(msg, "top-center", "error");
        })
        .finally(() => {
          row.saving = false;
        });
    },
    submit() {
      if (!this.url) return;
      this.loading = true;
      this.result = null;
      this.$req
        .post(
          "gallery/index",
          {
            url: this.url.trim(),
            master_event: this.masterEvent || undefined,
            year: this.year || undefined,
          },
          this.$bus.headers
        )
        .then((r) => {
          this.result = r.data;
          this.url = "";
          this.$bus.notify("Gallery indexed");
          this.loadExisting();
        })
        .catch((e) => {
          let msg =
            (e.response && e.response.data && e.response.data.message) ||
            e.message ||
            "Failed to index gallery";
          this.$bus.notify(msg, "top-center", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.gi_input {
  display: block;
  width: 100%;
  padding: 8px 12px;
  margin-top: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}
.gi_result {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 16px;
  background: #fafafa;
}
.gi_cover {
  width: 220px;
  height: 140px;
  border-radius: 4px;
  margin-top: 8px;
}
</style>
